import { Checkmark, Edit, xmark } from 'ck-assets';
import {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { MutableRefObject, useLayoutEffect, useState } from 'react';

import { ErrorMessages } from 'ck-components';
import { FormLabelWithCount } from './FormLabel/FormLabelWithCount';
import { ReactSVG } from 'react-svg';

export interface FormLinkItem {
  label: string;
  url: string;
}

interface FormLinksInputProps<T extends FieldValues> {
  name: Path<T>;
  parentForm: UseFormReturn<T>;
  label: string;
  description?: string;
  maxLinks?: number;
  /**
   * Receives a function that commits whatever is currently typed but not yet added to the list.
   * Call it before submitting the parent form, otherwise a link the user typed and never
   * confirmed is silently discarded. Returns false when the pending input is incomplete or
   * invalid, in which case the field has shown an error and the submit should be abandoned.
   */
  flushRef?: MutableRefObject<(() => boolean) | null>;
}

/**
 * Adds the scheme a bare host is missing, leaving an explicit http:// alone - a host that is
 * genuinely http-only would break if it were upgraded. Stored links are opened directly by the
 * app, and a scheme-less url has no handler to resolve to, so this is what makes the saved value
 * openable rather than just tidier.
 */
const withScheme = (value: string) =>
  /^https?:\/\//i.test(value) ? value : `https://${value}`;

const isValidUrl = (value: string) => {
  try {
    return new URL(withScheme(value)).hostname.includes('.');
  } catch {
    return false;
  }
};

/**
 * An input for building up a short list of labeled links. Links are validated and committed
 * to the list by pressing Enter or clicking the checkmark, and can afterwards be edited or removed.
 */
export const FormLinksInput = <T extends FieldValues>({
  name,
  parentForm,
  label,
  description,
  maxLinks = 3,
  flushRef,
}: FormLinksInputProps<T>) => {
  const { control, setValue, setError, clearErrors, formState } = parentForm;
  const links =
    (useWatch({ control, name }) as FormLinkItem[] | undefined) ?? [];

  const [labelValue, setLabelValue] = useState('');
  const [urlValue, setUrlValue] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const resetInputs = () => {
    setLabelValue('');
    setUrlValue('');
    setEditingIndex(null);
  };

  /**
   * @returns true when there is nothing left pending - either the inputs were empty or their
   * contents were added to the list. False means the pending input is invalid and an error is
   * showing, so a caller flushing before submit should stop there.
   */
  const commit = (): boolean => {
    const trimmedLabel = labelValue.trim();
    const trimmedUrl = urlValue.trim();

    if (!trimmedLabel && !trimmedUrl) {
      resetInputs();
      clearErrors(name);
      return true;
    }

    if (!trimmedLabel) {
      setError(name, { type: 'manual', message: 'Ange en etikett' });
      return false;
    }

    if (!isValidUrl(trimmedUrl)) {
      setError(name, { type: 'manual', message: 'Ange en giltig länk' });
      return false;
    }

    // Normalised here rather than at send time so the committed row shows exactly what will be
    // stored, instead of a bare host that quietly gains a scheme on the next reload.
    const newLink: FormLinkItem = {
      label: trimmedLabel,
      url: withScheme(trimmedUrl),
    };

    if (editingIndex !== null) {
      const updated = [...links];
      updated[editingIndex] = newLink;
      setValue(name, updated as PathValue<T, Path<T>>);
    } else {
      setValue(name, [...links, newLink] as PathValue<T, Path<T>>);
    }

    resetInputs();
    clearErrors(name);
    return true;
  };

  // Re-registered on every render so the exposed function closes over current input state
  // rather than whatever was typed at mount. Layout rather than passive: the ref is read from
  // a click handler, and a passive effect can still be queued behind the click that reads it,
  // which would flush the input as it was one keystroke ago.
  useLayoutEffect(() => {
    if (!flushRef) return;
    flushRef.current = commit;
    return () => {
      flushRef.current = null;
    };
  });

  const handleEdit = (idx: number) => {
    setLabelValue(links[idx].label);
    setUrlValue(links[idx].url);
    setEditingIndex(idx);
    clearErrors(name);
  };

  const handleDelete = (idx: number) => {
    setValue(name, links.filter((_, i) => i !== idx) as PathValue<T, Path<T>>);
    if (editingIndex === idx) {
      resetInputs();
    }
  };

  const canAddMore = links.length < maxLinks || editingIndex !== null;

  return (
    <div>
      <FormLabelWithCount name={name} label={label} description={description} />
      <div className='flex flex-col gap-2'>
        {canAddMore && (
          <div className='flex gap-2'>
            <input
              type='text'
              value={labelValue}
              placeholder='Namn, t.ex. Instagram'
              onChange={(e) => {
                setLabelValue(e.target.value);
                clearErrors(name);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commit();
                }
              }}
              className='h-14 w-1/3 rounded border border-gray-300 px-4 focus:outline-sky-500'
            />
            <div className='relative flex-1'>
              <input
                type='text'
                value={urlValue}
                placeholder='https://exempel.se'
                onChange={(e) => {
                  setUrlValue(e.target.value);
                  clearErrors(name);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commit();
                  }
                }}
                className='h-14 w-full rounded border border-gray-300 pl-4 pr-14 focus:outline-sky-500'
              />
              <button
                type='button'
                onClick={commit}
                className='absolute top-1/2 right-3 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-green-500'
              >
                <ReactSVG src={Checkmark} />
              </button>
            </div>
          </div>
        )}

        {links.map((link, idx) => (
          <div
            key={`${link.url}-${idx}`}
            className='flex w-full items-center justify-between overflow-hidden rounded border border-gray-300 px-4 py-2'
          >
            <div className='flex flex-col overflow-hidden'>
              <span className='truncate font-medium'>{link.label}</span>
              <span className='truncate text-sm text-gray-400'>{link.url}</span>
            </div>
            <div className='flex shrink-0 gap-3 pl-3 text-gray-400'>
              <ReactSVG
                src={Edit}
                className='h-5 w-5 cursor-pointer hover:text-gray-600'
                onClick={() => handleEdit(idx)}
              />
              <ReactSVG
                src={xmark}
                className='h-5 w-5 cursor-pointer hover:text-rose-600'
                onClick={() => handleDelete(idx)}
              />
            </div>
          </div>
        ))}

        {!canAddMore && (
          <p className='text-sm text-gray-400'>
            Max {maxLinks} länkar tillåtna
          </p>
        )}
      </div>

      <ErrorMessages errors={formState.errors} name={name} />
    </div>
  );
};
